import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, FileText, MousePointerClick, Pin, UserPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Doodles";
import { StatCard, type Daily } from "@/components/home/StatCard";
import { WeekStrip, type Day } from "@/components/home/WeekStrip";
import { DraftsList, PinsDue, type DraftRow, type DuePin } from "@/components/home/DueLists";
import { SalesAnalytics } from "@/components/home/SalesAnalytics";

interface Home {
  today: string; season: string;
  counts: Record<"pins_due" | "drafts" | "drafts_due" | "drafts_late" | "photos_pending" | "signups_today" | "signups_7d" | "subscribers" | "clicks_today" | "clicks_7d", number>;
  pins_due: DuePin[]; drafts: DraftRow[]; signups: Daily[]; clicks: Daily[]; week: Day[]; coming_up: { day: string; text: string }[];
}

const longDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const daysTo = (iso: string, today: string) => Math.round((Date.parse(iso) - Date.parse(today)) / 86400000);

export default function HomeDashboardPage() {
  const [h, setH] = useState<Home | null>(null);
  const load = useCallback(() => { api.get("/admin/home").then((r) => setH(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  if (!h) return <p className="text-sm text-[#23324A]/60" data-testid="home-loading">Loading your day…</p>;
  const c = h.counts;
  return (
    <div data-testid="home-dashboard">
      <PageHeader title="Today" kicker={`${longDate(h.today)} · ${h.season}`} />
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Pins to post" value={c.pins_due} sub="due today or earlier" icon={Pin} to="/admin/pins" tone="#BFE0F5" testId="stat-pins" />
        <StatCard label="Drafts" value={c.drafts} sub={c.drafts_due ? `${c.drafts_due} due by today` : "waiting for you"} icon={FileText} to="/admin/articles" tone="#F6D776" testId="stat-drafts" />
        <StatCard label="Signups" value={c.signups_today} sub={`today · ${c.signups_7d} this week · ${c.subscribers} total`} icon={UserPlus} to="/admin" tone="#E2F5E0" testId="stat-signups" trend={h.signups} />
        <StatCard label="Clicks" value={c.clicks_today} sub={`today · ${c.clicks_7d} this week`} icon={MousePointerClick} to="/admin/affiliate" tone="#F9C6D3" testId="stat-clicks" trend={h.clicks} />
        <StatCard label="Reader photos" value={c.photos_pending} sub="to review" icon={Camera} to="/admin/photos" tone="#FFD8B0" testId="stat-photos" />
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.25fr_1fr]">
        <div className="space-y-6">
          <WeekStrip week={h.week} />
          {!!h.coming_up.length && (
            <section className="calm-card p-5 sm:p-6" data-testid="home-coming-up">
              <h2 className="font-display text-xl font-black text-[#23324A]">Coming up</h2>
              <ul className="mt-3 grid gap-3 sm:grid-cols-3">
                {h.coming_up.map((e) => (
                  <li key={e.day} className="rounded-2xl bg-[#FDFBF7] p-4" data-testid={`coming-up-${e.day}`}>
                    <p className="font-display text-lg font-black text-[#23324A]">{e.text}</p>
                    <p className="text-xs font-semibold text-[#23324A]/60">in {daysTo(e.day, h.today)} days</p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
        <div className="space-y-6">
          <DraftsList drafts={h.drafts} total={c.drafts} today={h.today} />
          <PinsDue pins={h.pins_due} total={c.pins_due} today={h.today} onDone={load} />
        </div>
      </div>
      <SalesAnalytics />
    </div>
  );
}
